import { useState, useEffect } from "react";
import { Hash, Loader2, Save, Send, RotateCcw, X, MessageSquare, Lock, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getSlackCommsSettings, saveSlackCommsSettings, previewSlackCommsEvent, getDirectory, getSlackWorkspaceUsers } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const NONE = "__none__";

function EventRow({ ev, users, expanded, onToggleExpand, onChange, onPreview, previewing }) {
  const changed = ev.template !== ev.default_template;
  const addTag = (email) => {
    if (!email || ev.tag_emails.includes(email)) return;
    onChange({ ...ev, tag_emails: [...ev.tag_emails, email] });
  };
  return (
    <div className={`border rounded-lg ${ev.enabled ? "bg-white" : "bg-slate-50 opacity-70"}`} data-testid={`slack-comms-event-row-${ev.key}`}>
      <div className="flex items-center gap-3 p-3">
        <Switch
          checked={ev.enabled}
          onCheckedChange={(v) => onChange({ ...ev, enabled: v })}
          data-testid={`slack-comms-event-toggle-${ev.key}`}
        />
        <button type="button" className="flex-1 flex items-center gap-2 text-left" onClick={onToggleExpand}
          data-testid={`slack-comms-event-expand-${ev.key}`}>
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className="font-medium text-sm">{ev.label}</span>
          {ev.parent && <Badge variant="outline" className="text-[10px]">Thread parent</Badge>}
          {changed && <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]" variant="outline">edited</Badge>}
          {ev.tag_emails.length > 0 && <Badge variant="secondary" className="text-[10px]">@{ev.tag_emails.length}</Badge>}
        </button>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-0 space-y-3 border-t bg-slate-50/50">
          <div className="space-y-1 pt-3">
            <Label className="text-xs">Message template</Label>
            <Textarea
              value={ev.template}
              onChange={(e) => onChange({ ...ev, template: e.target.value })}
              rows={2}
              className="text-sm font-mono"
              data-testid={`slack-comms-template-${ev.key}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">@Tag portal users (mentioned when this event fires)</Label>
            <div className="flex flex-wrap items-center gap-2">
              {ev.tag_emails.map((email) => {
                const u = users.find((x) => x.email === email);
                return (
                  <Badge key={email} variant="secondary" className="gap-1 pr-1" data-testid={`slack-comms-tag-${ev.key}-${email}`}>
                    {u?.name || email}
                    <button type="button" onClick={() => onChange({ ...ev, tag_emails: ev.tag_emails.filter((e) => e !== email) })}
                      className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                );
              })}
              <Select value="" onValueChange={addTag}>
                <SelectTrigger className="h-7 w-44 text-xs" data-testid={`slack-comms-tag-select-${ev.key}`}>
                  <SelectValue placeholder="+ Add user to tag" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter((u) => u.email && !ev.tag_emails.includes(u.email)).map((u) => (
                    <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            {changed && (
              <Button size="sm" variant="outline" onClick={() => onChange({ ...ev, template: ev.default_template })}
                data-testid={`slack-comms-reset-${ev.key}`}>
                <RotateCcw className="w-3 h-3 mr-1" /> Reset to default
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onPreview} disabled={previewing}
              data-testid={`slack-comms-preview-${ev.key}`}>
              {previewing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Preview to channel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SlackCommsSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewingKey, setPreviewingKey] = useState(null);
  const [channel, setChannel] = useState("");
  const [ausChannel, setAusChannel] = useState("");
  const [dmEmail, setDmEmail] = useState("");
  const [dmEmailAus, setDmEmailAus] = useState("");
  const [events, setEvents] = useState([]);
  const [eventsAus, setEventsAus] = useState([]);
  const [region, setRegion] = useState("UK");
  const [variables, setVariables] = useState([]);
  const [users, setUsers] = useState([]);
  const [slackUsers, setSlackUsers] = useState([]);
  const [expandedKey, setExpandedKey] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, dir, slack] = await Promise.all([
        getSlackCommsSettings(),
        getDirectory().catch(() => []),
        getSlackWorkspaceUsers().catch(() => ({ users: [] })),
      ]);
      setChannel(cfg.channel || "");
      setAusChannel(cfg.aus_channel || "");
      setDmEmail(cfg.test_payment_dm_email || "");
      setDmEmailAus(cfg.test_payment_dm_email_aus || "");
      setEvents(cfg.events || []);
      setEventsAus(cfg.events_aus || []);
      setVariables(cfg.variables || []);
      setUsers(dir.filter((u) => u.is_active !== false));
      setSlackUsers(slack.users || []);
      setDirty(false);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Failed to load Slack comms settings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateEvent = (updated) => {
    const setter = region === "AUS" ? setEventsAus : setEvents;
    setter((prev) => prev.map((ev) => (ev.key === updated.key ? updated : ev)));
    setDirty(true);
  };
  const toPatch = (list) => Object.fromEntries(list.map((ev) => [ev.key, {
    enabled: ev.enabled,
    template: ev.template === ev.default_template ? "" : ev.template,
    tag_emails: ev.tag_emails,
  }]));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        channel,
        aus_channel: ausChannel,
        test_payment_dm_email: dmEmail,
        test_payment_dm_email_aus: dmEmailAus,
        events: toPatch(events),
        events_aus: toPatch(eventsAus),
      };
      const res = await saveSlackCommsSettings(payload);
      setEvents(res.events || []);
      setEventsAus(res.events_aus || []);
      setDirty(false);
      const unresolved = Object.entries(res.unresolved_tags || {});
      if (unresolved.length) {
        const detail = unresolved
          .map(([key, emails]) => `${key}: ${emails.join(", ")}`)
          .join(" · ");
        toast.warning(
          `Saved — but these tags don't match any Slack account and will NOT notify: ${detail}. Pick the "(Slack)" entry for that person instead.`,
          { duration: 15000 }
        );
      } else {
        toast.success("Slack communication flow saved");
      }
    } catch (e) {
      toast.error(errorDetailMessage(e, "Failed to save settings"));
    } finally {
      setSaving(false);
    }
  };

  const preview = async (key) => {
    setPreviewingKey(key);
    try {
      if (dirty) await save();
      await previewSlackCommsEvent(key, region);
      toast.success(`Preview posted to ${region === "AUS" ? ausChannel || "#au-funding" : channel}`);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Preview failed"));
    } finally {
      setPreviewingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading Slack comms flow…
      </div>
    );
  }

  const regionEvents = region === "AUS" ? eventsAus : events;
  const stages = [...new Set(regionEvents.map((e) => e.stage))];
  const tagCount = (list) => list.reduce((n, e) => n + (e.tag_emails?.length || 0), 0);

  // Tag/DM dropdown options: portal users + Slack workspace users not already
  // covered by a portal account (their Slack email is what actually resolves
  // to a notifying @mention).
  const tagOptions = [
    ...users,
    ...slackUsers
      .filter((su) => su.email && !users.some((u) => (u.email || "").toLowerCase() === su.email.toLowerCase()))
      .map((su) => ({ id: `slack-${su.id}`, email: su.email, name: `${su.real_name || su.name} (Slack)` })),
  ];

  return (
    <div className="space-y-6" data-testid="slack-comms-settings">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-purple-600" />
            Slack communication flow
          </CardTitle>
          <CardDescription>
            Every event in a deal's funding journey posts as a threaded reply under one parent message per deal —
            keeping the channel clean. Toggle events on/off, re-word the message and choose who gets @tagged.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Funding channel (UK)</Label>
              <Input value={channel} onChange={(e) => { setChannel(e.target.value); setDirty(true); }}
                placeholder="#uk-funding" className="h-9" data-testid="slack-comms-channel-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Funding channel (Australia)</Label>
              <Input value={ausChannel} onChange={(e) => { setAusChannel(e.target.value); setDirty(true); }}
                placeholder="#au-funding" className="h-9" data-testid="slack-comms-aus-channel-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <Lock className="w-3 h-3" /> Nominal verifier — UK (receives the hidden GoCardless amount by DM)
              </Label>
              <Select value={dmEmail || NONE} onValueChange={(v) => { setDmEmail(v === NONE ? "" : v); setDirty(true); }}>
                <SelectTrigger className="h-9" data-testid="slack-comms-dm-select">
                  <SelectValue placeholder="Choose a portal user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Not set —</SelectItem>
                  {tagOptions.filter((u) => u.email).map((u) => (
                    <SelectItem key={u.id} value={u.email}>{u.name || u.email} ({u.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <Lock className="w-3 h-3" /> Nominal verifier — Australia (receives the hidden Capay amount by DM)
              </Label>
              <Select value={dmEmailAus || NONE} onValueChange={(v) => { setDmEmailAus(v === NONE ? "" : v); setDirty(true); }}>
                <SelectTrigger className="h-9" data-testid="slack-comms-dm-aus-select">
                  <SelectValue placeholder="Choose a portal user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Not set —</SelectItem>
                  {tagOptions.filter((u) => u.email).map((u) => (
                    <SelectItem key={u.id} value={u.email}>{u.name || u.email} ({u.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">AU nominal amounts only ever go to this person — never to the UK verifier.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">Template variables:</span>
            {variables.map((v) => (
              <code key={v} className="text-[11px] px-1.5 py-0.5 bg-slate-100 rounded border">{v}</code>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving || !dirty} data-testid="slack-comms-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {dirty ? "Save changes" : "Saved"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-white p-3" data-testid="slack-comms-region-switch">
        <span className="text-sm font-medium">Event comms for</span>
        <div className="inline-flex rounded-md border overflow-hidden">
          {[["UK", "🇬🇧 United Kingdom", channel || "#uk-funding", tagCount(events)], ["AUS", "🇦🇺 Australia", ausChannel || "#au-funding", tagCount(eventsAus)]].map(([key, label, ch, tags]) => (
            <button key={key} type="button" onClick={() => { setRegion(key); setExpandedKey(null); }}
              className={`px-3 py-1.5 text-sm flex items-center gap-2 ${region === key ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
              data-testid={`slack-comms-region-${key.toLowerCase()}`}>
              {label} <span className={`text-[11px] font-mono ${region === key ? "text-slate-300" : "text-muted-foreground"}`}>{ch} · @{tags}</span>
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">Each desk has its own on/off, wording and @tags per event — nothing is shared between regions.</span>
      </div>

      {stages.map((stage) => (
        <Card key={`${region}-${stage}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-500" />
              {stage}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {regionEvents.filter((e) => e.stage === stage).map((ev) => (
              <EventRow
                key={`${region}-${ev.key}`}
                ev={ev}
                users={tagOptions}
                expanded={expandedKey === ev.key}
                onToggleExpand={() => setExpandedKey(expandedKey === ev.key ? null : ev.key)}
                onChange={updateEvent}
                onPreview={() => preview(ev.key)}
                previewing={previewingKey === ev.key}
              />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
